import React, { useEffect, useMemo, useRef, useState } from "react";
import "./Services.css";

// i18n
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

// Swiper
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y, Keyboard } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

// Images
import eventImg from "../../assets/industries/event-security.jpg";
import residentialImg from "../../assets/VISION_SECURITY_GCK_EVENT-181.jpeg";
import businessImg from "../../assets/industries/business-security.jpg";
import trafficImg from "../../assets/industries/traffic-control.jpg";
import fireImg from "../../assets/industries/fire-surveillance.webp";
import hospitalityImg from "../../assets/industries/hospitality-services.jpeg";
import luxuryImg from "../../assets/industries/luxury-security.png";
import lossPrevImage from "../../assets/industries/loss_prevention.jpeg";
import interventionServices from "../../assets/IMG_0556 (1).jpeg";
import constSiteSecurity from "../../assets/industries/constru_site_security.jpeg";
import patrolServices from "../../assets/industries/patrol_services.jpg";
import VIPsecurityServices from "../../assets/industries/VIP_security_services.jpg";
import SecurityCameras from "../../assets/industries/Security-Cameras.jpg";
import luxuryTansport from "../../assets/industries/luxuryTansport.jpg";
import ontarioProvince from "../../assets/Ontario-Province.png";
import quebecProvince from "../../assets/QuebecProvince.png";

/* ---- tiny reveal-on-scroll hook ---- */
function useReveal(options = { threshold: 0.18, rootMargin: "0px 0px -10% 0px" }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!ref.current || inView) return;
    if (!("IntersectionObserver" in window)) {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setInView(true);
        io.disconnect();
      }
    }, options);
    io.observe(ref.current);
    return () => io.disconnect();
  }, [inView, options]);

  return [ref, inView];
}

/**
 * Base list — keep your original titles as fallbacks.
 * We'll map each slug to an i18n key: services.<slug>
 * e.g. services.event-security, services.residential-security, etc.
 */
const industries = [
  { title: "EVENT SECURITY", slug: "event-security", image: eventImg },
  { title: "RESIDENTIAL SECURITY", slug: "residential-security", image: residentialImg },
  { title: "BUSINESS SECURITY", slug: "business-security", image: businessImg },
  { title: "TRAFFIC CONTROL", slug: "traffic-control", image: trafficImg },
  { title: "FIRE SURVEILLANCE", slug: "fire-surveillance", image: fireImg },
  { title: "HOSPITALITY SERVICES", slug: "hospitality-services", image: hospitalityImg },
  { title: "LUXURY SECURITY", slug: "luxury-security", image: luxuryImg },
  { title: "LOSS PREVENTION", slug: "loss-prevention", image: lossPrevImage },
  { title: "INTERVENTION SERVICES", slug: "intervention-services", image: interventionServices },
  { title: "CONSTRUCTION SITE SECURITY", slug: "construction-site-services", image: constSiteSecurity },
  { title: "PATROL SERVICES", slug: "patrol-services", image: patrolServices },
  { title: "VIP SECURITY SERVICES", slug: "VIP-security-services", image: VIPsecurityServices },
  { title: "CAMERAS AND INSTALLATIONS", slug: "cameras-installations", image: SecurityCameras },
  { title: "LUXURY TRANSPORTATION", slug: "luxury-transportation", image: luxuryTansport },
];

export default function Services() {
  const { t, i18n } = useTranslation();
  const location = useLocation();

  const [q, setQ] = useState("");
  const [swiperInstance, setSwiperInstance] = useState(null);
  const [isBeginning, setIsBeginning] = useState(true);
  const [isEnd, setIsEnd] = useState(false);

  // localized label getter for each card title
  const getTitle = (item) => {
    const slug = item.slug;
    const baseKey = `services.${slug}`;

    // 1) explicit .title
    const explicit = t(`${baseKey}.title`, { defaultValue: undefined });
    if (typeof explicit === "string" && explicit !== `${baseKey}.title`) return explicit;

    // 2) try base key (may be string or object)
    const translated = t(baseKey, { returnObjects: true });
    if (typeof translated === "string") {
      if (translated !== baseKey) return translated;
    } else if (translated && typeof translated === "object") {
      // debug: inspect the returned object shape in console
      console.debug("i18n services object for", slug, translated);

      // common nested fields
      if (typeof translated.title === "string") return translated.title;
      if (translated.hero && (translated.hero.title0 || translated.hero.title)) return translated.hero.title0 || translated.hero.title;
      if (translated.expertise && typeof translated.expertise.title === "string") return translated.expertise.title;
      if (typeof translated.heading === "string") return translated.heading;
      if (typeof translated.name === "string") return translated.name;
      if (Array.isArray(translated) && translated.length && typeof translated[0] === "string") return translated[0];

      // fallback: find first string leaf in the object
      const findFirstString = (node) => {
        if (typeof node === "string") return node;
        if (Array.isArray(node)) {
          for (const v of node) {
            const r = findFirstString(v);
            if (r) return r;
          }
        } else if (node && typeof node === "object") {
          for (const k of Object.keys(node)) {
            const r = findFirstString(node[k]);
            if (r) return r;
          }
        }
        return null;
      };
      const first = findFirstString(translated);
      if (first) return first;
    }

    // 3) try alternate key shapes (camel / no-dash)
    const camel = slug.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    const altTitle = t(`services.${camel}.title`, { defaultValue: undefined });
    if (typeof altTitle === "string" && altTitle !== `services.${camel}.title`) return altTitle;
    const noDash = slug.replace(/-/g, "");
    const alt2 = t(`services.${noDash}.title`, { defaultValue: undefined });
    if (typeof alt2 === "string" && alt2 !== `services.${noDash}.title`) return alt2;

    // final fallback
    return item.title;
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return industries;
    return industries.filter((i) => getTitle(i).toLowerCase().includes(s));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, i18n.language]);

  // reveals
  const [headRef, headIn] = useReveal();
  const [carouselRef, carouselIn] = useReveal();

  // Handle swiper state changes
  const handleSlideChange = (swiper) => {
    setIsBeginning(swiper.isBeginning);
    setIsEnd(swiper.isEnd);
  };

  // Navigation handlers
  const handlePrevSlide = () => {
    if (swiperInstance) swiperInstance.slidePrev();
  };
  const handleNextSlide = () => {
    if (swiperInstance) swiperInstance.slideNext();
  };

  // prefix FR routes when language is fr
  const langPrefix = i18n.language === "fr" ? "/fr" : "";

  return (
    <section id="services" aria-labelledby="svc-heading">
      <div className="svc__container">
        <div
          ref={headRef}
          className={`svc__header reveal reveal--up ${headIn ? "in" : ""}`}
        >
          <div className="svc__header-content">
            <div className="svc__title-group">
              <div className="svc__title-with-provinces">
                <img src={ontarioProvince} alt="Ontario Province" className="svc__province-image svc__province-left" />
                <h2 id="svc-heading" className="svc__heading reveal-item" style={{ transitionDelay: "80ms" }}>
                  {t("services.heading", { defaultValue: "Our Services" })}
                </h2>
                <img src={quebecProvince} alt="Quebec Province" className="svc__province-image svc__province-right" />
              </div>
              <div className="svc__accent-line reveal-item" style={{ transitionDelay: "120ms" }}></div>
            </div>

            <p className="svc__subhead reveal-item" style={{ transitionDelay: "170ms" }}>
              {t("services.subhead", {
                defaultValue: "Licensed guards, rapid deployment, and 24/7 support across Montreal & area."
              })}
            </p>

            {/* Search with navigation arrows */}
            <div className="svc__searchWrap reveal-item" style={{ transitionDelay: "240ms" }}>
              <button
                className="svc__nav-arrow svc__nav-prev"
                onClick={handlePrevSlide}
                disabled={isBeginning}
                aria-label={t("services.prev", { defaultValue: "Previous services" })}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
                </svg>
              </button>

              <div className="svc__search-input-wrapper">
                <svg
                  className="svc__searchIcon"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                  focusable="false"
                >
                  <path
                    d="M15.5 14h-.79l-.28-.27A6.471 6.471 0 0 0 16 9.5 6.5 6.5 0 1 0 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5Zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14Z"
                    fill="currentColor"
                  />
                </svg>
                <input
                  className="svc__search"
                  type="search"
                  placeholder={t("services.searchPlaceholder", { defaultValue: "Search services…" })}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  aria-label={t("services.searchAria", { defaultValue: "Search services" })}
                />
              </div>

              <button
                className="svc__nav-arrow svc__nav-next"
                onClick={handleNextSlide}
                disabled={isEnd}
                aria-label={t("services.next", { defaultValue: "Next services" })}
              >
                <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="svc__empty">
            {t("services.noMatches", { defaultValue: "No matches. Try another term." })}
          </div>
        ) : (
          <div
            ref={carouselRef}
            className={`svc__carousel-wrap reveal reveal--up ${carouselIn ? "in" : ""}`}
          >
            <Swiper
              className="svc__carousel"
              modules={[Navigation, A11y, Keyboard]}
              onSwiper={(swiper) => {
                setSwiperInstance(swiper);
                setIsBeginning(swiper.isBeginning);
                setIsEnd(swiper.isEnd);
              }}
              onSlideChange={handleSlideChange}
              navigation={false}
              keyboard={{ enabled: true }}
              a11y={{ enabled: true }}
              spaceBetween={18}
              loop={filtered.length > 2}
              breakpoints={{
                0: { slidesPerView: 1, spaceBetween: 0 },
                480: { slidesPerView: 1, spaceBetween: 0 },
                640: { slidesPerView: 2, spaceBetween: 16 },
                900: { slidesPerView: 3, spaceBetween: 18 },
                1200: { slidesPerView: 4, spaceBetween: 20 },
              }}
            >
              {filtered.map(({ title, slug, image }, i) => {
                const displayTitle = getTitle({ title, slug });
                return (
                  <SwiperSlide key={slug} aria-label={displayTitle}>
                    <a
                      href={`${langPrefix}/services/${slug}`}
                      className="svc__card reveal-item"
                      style={{ transitionDelay: `${100 + (i % 6) * 80}ms` }}
                      aria-label={t("services.learnMoreAria", {
                        defaultValue: "Learn more about {{title}}",
                        title: displayTitle
                      })}
                    >
                      <img 
                        className={`svc__img ${slug === 'residential-security' || slug === 'luxury-security' || slug === 'intervention-services' ? 'svc__img--top' : ''}`}
                        src={image} 
                        alt={displayTitle} 
                        loading="lazy" 
                      />
                      <div className="svc__shade" />
                      <div className="svc__content">
                        <span className="svc__pill">{t("services.explore", { defaultValue: "Explore" })}</span>
                        <h3 className="svc__title">{displayTitle}</h3>
                      </div>
                    </a>
                  </SwiperSlide>
                );
              })}
            </Swiper>
          </div>
        )}
      </div>
    </section>
  );
}
